import { useRef, useCallback, useEffect } from 'react';

const SCROLL_STEP = 280; // ~2 columns
const LEADING_CONTEXT_WEEKS = 2;

export function useGanttScroll() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);

  const scrollBy = useCallback((delta: number) => {
    containerRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const scrollToWeek = useCallback(
    (
      weekIndex: number,
      weekWidths: number[],
      leadingContextWeeks: number = LEADING_CONTEXT_WEEKS
    ) => {
      const container = containerRef.current;
      if (!container || weekIndex < 0 || weekIndex >= weekWidths.length) return;

      const startIndex = Math.max(0, weekIndex - leadingContextWeeks);
      let offset = 0;
      for (let i = 0; i < startIndex; i++) {
        offset += weekWidths[i];
      }

      container.scrollTo({ left: offset, behavior: 'instant' });
    },
    []
  );

  const scrollToStart = useCallback(() => {
    containerRef.current?.scrollTo({ left: 0, behavior: 'instant' });
  }, []);

  const scrollLeft = useCallback(() => {
    scrollBy(-SCROLL_STEP);
  }, [scrollBy]);
  const scrollRight = useCallback(() => {
    scrollBy(SCROLL_STEP);
  }, [scrollBy]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartX.current = container.scrollLeft;
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const dx = e.clientX - dragStartX.current;
    containerRef.current.scrollLeft = scrollStartX.current - dx;
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const container = containerRef.current;
    if (container) {
      container.style.cursor = '';
      container.style.userSelect = '';
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when a modal or drawer has locked page scroll
      if (document.body.style.overflow === 'hidden') return;

      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        scrollBy(-SCROLL_STEP);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        scrollBy(SCROLL_STEP);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [scrollBy]);

  return {
    containerRef,
    scrollLeft,
    scrollRight,
    scrollToWeek,
    scrollToStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  };
}
